import { getMapTelemetry } from 'browser/map-search/services/mapTelemetry';
import { TDevice } from '@cian/utils';
import { getLiquiditySourceParam } from 'shared/map-search/utils/liquiditySource';

import { resetSelectedFeature, selectFeature } from './selectFeature';
import { toListingUrl } from '../../mappers/queryString';
import { selectIsUserAgent } from '../../selectors/user';
import { TFeature } from '../../types/map';
import { IApplicationState, TThunkAction } from '../../types/redux';
import { convertBboxToString } from '../../utils/convertBboxToString';
import { getNewbuildingQuery } from '../../utils/queryString';
import { removeNewbuildingsFromUrl } from '../../utils/removeNewbuildingsFromUrl';
import { changeSearchResultsUrl, openSearchResultsSidebar } from '../searchResults';
import { markAsViewed } from '../viewed/markAsViewed';

export interface IOpenFeatureParams {
  feature: TFeature;
  precision: number;
  isDetailed: boolean;
  deviceType: TDevice;
  source?: string;
  isDotPin?: boolean;
}

/** Открывает детальную информацию о выбранном пине (объекте) на карте. */
export function openFeature({
  feature,
  precision,
  isDetailed,
  deviceType,
  source,
  isDotPin,
}: IOpenFeatureParams): TThunkAction<Promise<void>> {
  return async (dispatch, getState, context) => {
    getMapTelemetry().trackPinResultsAttempt();

    const baseUrl = context.config.get<string>('mobileSearchFrontend.browserUrl');
    const currentSubdomain = context.custom.subdomain;
    const featureSubdomain = feature.properties.subdomain;
    const sourceParam = source ? `&source=${encodeURIComponent(source)}` : '';
    const isMobile = deviceType !== 'desktop';
    const liquiditySourceParam = getLiquiditySourceParam({
      deviceType,
      config: context.config,
      feature,
    });
    const qs = getFeatureQueryString(feature, getState(), isDetailed);
    const pinTypeParam = getPinTypeParam(feature);
    const pinOriginParams = getPinOriginParams(feature);

    const url = `${baseUrl}v1/get-results-for-map?${qs}&parentOrigin=${window.location.origin}&isGlow=${feature.properties.isGlow || false}${pinTypeParam}${pinOriginParams}${sourceParam}${liquiditySourceParam}`;
    const urlWithoutNewbuildings = removeNewbuildingsFromUrl(url);

    if (isMobile) {
      const urlFromQs = `${toListingUrl(qs)}&origin=map&isGlow=${feature.properties.isGlow || false}${pinTypeParam}${pinOriginParams}${sourceParam}${liquiditySourceParam}`;
      const mobileUrlWithoutNewbuildings = removeNewbuildingsFromUrl(urlFromQs);
      const mobileUrl = `//${window.location.hostname.replace(
        `${currentSubdomain}.`,
        `${featureSubdomain}.`,
      )}${mobileUrlWithoutNewbuildings}`;

      const openedWindow = window.open(mobileUrl, '_blank');

      if (!openedWindow) {
        getMapTelemetry().trackPinPopupBlocked();

        context.logger.warning('Новая вкладка с выдачей заблокирована браузером, открываем в текущей', {
          domain: 'actions/features/openFeature',
          details: { url: mobileUrl },
        });

        window.location.assign(mobileUrl);
      }
    }

    const selectFeaturePromise = dispatch(selectFeature(feature, precision));

    if (isMobile) {
      setTimeout(() => dispatch(resetSelectedFeature(feature.id)), 1000);
    }

    await selectFeaturePromise;
    dispatch(changeSearchResultsUrl(urlWithoutNewbuildings));
    dispatch(openSearchResultsSidebar(isDotPin));

    if (!feature.properties.isViewed && (!isDotPin || feature.properties.count === 1)) {
      dispatch(markAsViewed({ feature, precision, isDetailed }));
    }
  };
}

export function getFeatureQueryString(feature: TFeature, state: IApplicationState, isDetailed: boolean) {
  const {
    bbox: featureBBox,
    isExtended,
    newbuilding,
    isNewbuildingPromo,
    hasNewobject,
    isAnyFromDeveloper,
    specialPromo,
  } = feature.properties;
  const { queryString, extendedQueryString, nonGeoQueryString, mlSearchSessionGuid } = state;
  const userIsAgent = selectIsUserAgent(state);
  const qs = isExtended && extendedQueryString ? extendedQueryString : queryString;

  const isLeadsBannerVisibleParam = !userIsAgent ? '&isLeadsBannerVisible=1' : '';
  const isPreselectedFromDeveloper = hasNewobject && isAnyFromDeveloper;
  const isPinWithPrice = isDetailed || isNewbuildingPromo || specialPromo ? '&isPinWithPrice=1' : '';
  const bboxString = convertBboxToString(featureBBox);
  const bboxFilter = bboxString ? `&bbox=${bboxString}` : '';

  if (newbuilding) {
    const houseIds = newbuilding.houseId ? [newbuilding.houseId] : undefined;
    const newbuildingQuery = getNewbuildingQuery({
      qs: nonGeoQueryString,
      newbuilding,
      houseIds,
      isNewbuildingPromo,
    });
    const customFilter = isPreselectedFromDeveloper ? '&preselected_from_developer=1' : '&preselected_from_developer=0';

    return `${newbuildingQuery}&isNavigationDisabled=1${isLeadsBannerVisibleParam}${customFilter}${isPinWithPrice}${bboxFilter}`;
  }

  return `${qs}${bboxFilter}${mlSearchSessionGuid ? `&mlSearchSessionGuid=${mlSearchSessionGuid}` : ''}&isNavigationDisabled=1${isLeadsBannerVisibleParam}`;
}

function getPinTypeParam(feature: TFeature): string {
  if (feature.properties.isNewbuildingMainPin) {
    return '&pinType=NewBuildingJK';
  }

  if (feature.properties.specialPromo !== undefined) {
    return '&pinType=SpecialPromo';
  }

  if (feature.properties.newbuilding?.isAnyFicheringPlus) {
    return '&pinType=NewBuildingPromo';
  }

  return '&pinType=Regular';
}

/**
 * Признаки пина, по которому открыта выдача, для pageview выдачи (is_from_specproject_pin / is_from_jk_pin).
 * В отличие от pinType это независимые флаги: пин ЖК может одновременно быть спецпроектом.
 */
function getPinOriginParams(feature: TFeature): string {
  const isFromSpecprojectPin = Boolean(feature.properties.specialPromo);
  const isFromJkPin = Boolean(feature.properties.isNewbuildingMainPin);

  return `&isFromSpecprojectPin=${isFromSpecprojectPin}&isFromJkPin=${isFromJkPin}`;
}
